import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config/config';
import { getDisplayStatus, getStatusSource } from '../types/flight';

const STATUS_OVERRIDE_OPTIONS = [
  'Boarding Complete',
  'Boarding',
  'Baggage Delay',
  'Closed',
  'Final Call',
  'Gate Closed',
  'Go To Gate'
];

/**
 * StatusOverrideEditor
 * Admin page for managing manual status overrides for both arrivals and departures
 */
const StatusOverrideEditor = () => {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useState({
    flightNumber: '',
    from: '',
    to: '',
    origin: '',
    destination: '',
    direction: ''
  });
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Modal state
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [statusValue, setStatusValue] = useState('');

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const authData = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = authData.token;

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const params = new URLSearchParams();
      if (searchParams.flightNumber) params.append('flightNumber', searchParams.flightNumber);
      if (searchParams.from) params.append('from', new Date(searchParams.from).toISOString());
      if (searchParams.to) params.append('to', new Date(searchParams.to).toISOString());
      if (searchParams.origin) params.append('origin', searchParams.origin);
      if (searchParams.destination) params.append('destination', searchParams.destination);
      if (searchParams.direction) params.append('direction', searchParams.direction);

      const response = await fetch(`${API_BASE_URL}/api/flights?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to fetch flights: ${response.status}`);
      }

      setFlights(data.data || []);
    } catch (err) {
      console.error('Error fetching flights:', err);
      setError(err.message || 'Failed to fetch flights. Please try again.');
      setFlights([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({ ...prev, [field]: value }));
  };

  const handleEditClick = (flight) => {
    setSelectedFlight(flight);
    setStatusValue(flight.statusOverride?.value || '');
    setModalError('');
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedFlight(null);
    setStatusValue('');
    setModalError('');
  };

  const handleSave = async () => {
    if (!selectedFlight) return;

    setModalLoading(true);
    setModalError('');

    try {
      const authData = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = authData.token;

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const requestBody = {
        status: { value: statusValue.trim() || null }
      };

      const response = await fetch(`${API_BASE_URL}/api/flights/${selectedFlight._id}/overrides`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to update override: ${response.status}`);
      }

      // Update the flight in the list
      setFlights(prev =>
        prev.map(f => f._id === data.data._id ? { ...f, ...data.data } : f)
      );

      setError('');
      handleModalClose();
    } catch (err) {
      console.error('Error updating status override:', err);
      setModalError(err.message || 'Failed to update status override.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleReset = () => {
    setSearchParams({
      flightNumber: '',
      from: '',
      to: '',
      origin: '',
      destination: '',
      direction: ''
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const darkFieldSx = {
    '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
    '& .MuiInputLabel-root': { color: '#999' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#000', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
        Status Override Editor
      </Typography>

      {/* Search Filters */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#1a1a1a' }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#fff' }}>
          Search Filters
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Flight Number"
              placeholder="e.g., AC123"
              value={searchParams.flightNumber}
              onChange={(e) => handleInputChange('flightNumber', e.target.value)}
              size="small"
              sx={darkFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Type"
              value={searchParams.direction}
              onChange={(e) => handleInputChange('direction', e.target.value)}
              size="small"
              sx={darkFieldSx}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="A">Arrivals</MenuItem>
              <MenuItem value="D">Departures</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Origin"
              placeholder="e.g., YUL"
              value={searchParams.origin}
              onChange={(e) => handleInputChange('origin', e.target.value)}
              size="small"
              sx={darkFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Destination"
              placeholder="e.g., YYZ"
              value={searchParams.destination}
              onChange={(e) => handleInputChange('destination', e.target.value)}
              size="small"
              sx={darkFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="datetime-local"
              label="From"
              value={searchParams.from}
              onChange={(e) => handleInputChange('from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={darkFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="datetime-local"
              label="To"
              value={searchParams.to}
              onChange={(e) => handleInputChange('to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={darkFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              sx={{ height: '40px' }}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              sx={{ height: '40px' }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Table */}
      <Paper sx={{ backgroundColor: '#1a1a1a' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #333' }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Flight Results ({flights.length})
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#2a2a2a' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Flight</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Scheduled</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Origin</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Destination</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && flights.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && flights.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#fff' }}>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      No flights found. Adjust your search filters and try again.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {flights.map((flight, index) => {
                const statusDisplay = getDisplayStatus(flight);
                const statusSource = getStatusSource(flight);

                return (
                  <TableRow key={flight._id} hover sx={{ backgroundColor: index % 2 === 0 ? '#1a1a1a' : '#000', '&:hover': { backgroundColor: '#2a2a2a' } }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                        {flight.airline}{flight.flightNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Chip
                        label={flight.direction === 'A' ? 'Arrival' : 'Departure'}
                        size="small"
                        color={flight.direction === 'A' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {formatTime(flight.scheduledTime)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {flight.route?.origin || '\u2014'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {flight.route?.destination || '\u2014'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {statusDisplay ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>{statusDisplay}</Typography>
                          {statusSource && (
                            <Chip
                              label={statusSource}
                              size="small"
                              variant="outlined"
                              color={statusSource === 'MANUAL' ? 'success' : 'default'}
                              sx={{ mt: 0.5, color: '#fff', borderColor: statusSource === 'MANUAL' ? '#4caf50' : '#999' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#999', fontWeight: 'bold' }}>{'\u2014'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(flight)}
                        sx={{ color: '#fff' }}
                        title="Edit Status Override"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Status Override Modal */}
      {selectedFlight && (
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { backgroundColor: '#fff', color: '#000' } }}
        >
          <DialogTitle sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
            Edit Status Override
          </DialogTitle>

          <DialogContent sx={{ mt: 2 }}>
            {/* Flight Summary */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Flight Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Flight:</strong> {selectedFlight.airline}{selectedFlight.flightNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Type:</strong>{' '}
                    <Chip
                      label={selectedFlight.direction === 'A' ? 'Arrival' : 'Departure'}
                      size="small"
                      color={selectedFlight.direction === 'A' ? 'primary' : 'secondary'}
                      sx={{ ml: 0.5 }}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Scheduled:</strong>{' '}
                    {selectedFlight.scheduledTime ? formatTime(selectedFlight.scheduledTime) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>{selectedFlight.direction === 'A' ? 'Origin' : 'Destination'}:</strong>{' '}
                    {selectedFlight.direction === 'A'
                      ? selectedFlight.route?.origin || 'N/A'
                      : selectedFlight.route?.destination || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Error */}
            {modalError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {modalError}
              </Alert>
            )}

            {/* Status Dropdown */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Status Override
              </Typography>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusValue}
                onChange={(e) => { setStatusValue(e.target.value); setModalError(''); }}
                disabled={modalLoading}
                helperText="Select empty to clear manual override. Cleared automatically when a live update arrives."
              >
                <MenuItem value="">
                  <em>None (use automatic status)</em>
                </MenuItem>
                {STATUS_OVERRIDE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5', borderTop: '1px solid #ddd' }}>
            <Button onClick={handleModalClose} disabled={modalLoading} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={() => { setStatusValue(''); setModalError(''); }}
              disabled={modalLoading}
              color="warning"
              variant="outlined"
            >
              Clear Override
            </Button>
            <Button
              onClick={handleSave}
              disabled={modalLoading}
              variant="contained"
              color="primary"
              startIcon={modalLoading && <CircularProgress size={16} color="inherit" />}
            >
              {modalLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default StatusOverrideEditor;
